const rgbRegex = /^rgba?\(([^,]+?),\s*([^,]+?),\s*([^,]+?)(?:,\s*[^)]+)?\)$/i;
const hslRegex = /^hsla?\((\d+),\s*([^,]+?),\s*([^,) \t]+)(?:,\s*([^,) \t]+))?\)$/i;
const validRegex = /^([rh#])/i;

/** Converts a percentage value to a value between [0-255] (inclusive).
 * @param value - Value to convert.
 * @returns A value between [0-255] (inclusive) representing the original percentage value.
 * @notes If the value is greater than 1, it is divided by 100 before conversion. */
function convertPctTo255(value: number) {
  let convertedValue = value;
  if (convertedValue > 1) {
    convertedValue /= 100;
  }

  return Math.min(Math.floor(convertedValue * 256), 255);
}

/** Convert Hue, Saturation, Lightness (HSL/HSV) values to their RGB components in the 0-255
 * color space.
 * @param hue - The hue of the color to convert. Should be between [0-360] (inclusive).
 * @param saturation - The saturation of the color to convert. Should be between [0-1]
 *        (inclusive).
 * @param lightness - The lightness (brightness) of the color to convert. Should be between
 *        [0-1] (inclusive).
 * @returns Returns an object with the r/g/b components
 *          specified as integers between [0-255] (inclusive).
 * @notes
 * Algorithm was adapted from https://stackoverflow.com/a/54014428/57787 which was inspired by the
 * alternative available at https://en.wikipedia.org/wiki/HSL_and_HSV#HSL_to_RGB_alternative. */
function convertHSLtoRGB(hue: number, saturation: number, lightness: number) {
  /* a & k are mathematical representations. */
  if (saturation === 0) {
    const value = convertPctTo255(lightness);
    return { red: value, green: value, blue: value };
  }

  const a = saturation * Math.min(lightness, 1 - lightness);
  const convert = (n: number) => {
    const k = (n + hue / 30) % 12;
    const minK = Math.min(k - 3, 9 - k, 1);
    const maxK = Math.max(minK, -1);
    return convertPctTo255(lightness - a * maxK);
  };

  return { red: convert(0), green: convert(8), blue: convert(4) };
}

/** Extract the RGB components from a given CSS color string.
 * @param {string} color - The input color to extract RGB components.
 * @returns {{red: 0, blue: 0, green: 0}} Returns an object containing the color string's RGB
 *          colors. All colors are in a range of [0-255] (inclusive).
 * @notes
 * This function supports shorthand hexadecimal, regular hexadecimal, rgb, rgba, hsl, and hsla
 * formats. CSS color names are not supported.
 */
export function getRGBComponents(color: string) {
  let red = 0;
  let green = 0;
  let blue = 0;

  const mode = validRegex.exec(color);
  if (!mode) {
    throw new Error('Unsupported color value provided. Please check the documentation.');
  }

  switch (mode[1].toLowerCase()) {
    case '#':
      const hex = color.substring(1);
      let rh = '';
      let gh = '';
      let bh = '';
      if (hex.length === 3 || hex.length === 4) {
        const double = (st: string, ix: number) => st.charAt(ix) + st.charAt(ix);

        rh = double(hex, 0);
        gh = double(hex, 1);
        bh = double(hex, 2);
      } else if (hex.length === 6 || hex.length === 8) {
        rh = hex.substr(0, 2);
        gh = hex.substr(2, 2);
        bh = hex.substr(4, 2);
      } else {
        throw new Error(
          'Unrecognized hex color string. Input must start with # and be 3, 6, or 8 characters ' +
            'long and should consist of hexadecimal values.',
        );
      }
      red = parseInt(rh, 16);
      green = parseInt(gh, 16);
      blue = parseInt(bh, 16);
      break;
    case 'r':
      const rgb = rgbRegex.exec(color);
      if (!rgb) {
        throw new Error(
          'Unregonized rgb/rgba color string. Input must be rgba? with at least three color ' +
            'parameters (with an optional alpha channel).',
        );
      }

      const redPct = rgb[1].indexOf('%') > -1;
      const greenPct = rgb[2].indexOf('%') > -1;
      const bluePct = rgb[3].indexOf('%') > -1;

      if (redPct !== greenPct || greenPct !== bluePct) {
        throw new Error(
          'Invalid rgb/rgba value combination. All values should be percents or a value between ' +
            '[0-255].',
        );
      }

      red = parseFloat(rgb[1]);
      green = parseFloat(rgb[2]);
      blue = parseFloat(rgb[3]);

      if (redPct) {
        red = convertPctTo255(red);
        green = convertPctTo255(green);
        blue = convertPctTo255(blue);
      }
      break;
    case 'h':
      const hsl = hslRegex.exec(color);
      if (!hsl) {
        throw new Error(
          'Unregonized hsl/hsla color string. Input must be hsla? with at least three color ' +
            'parameters (with an optional alpha channel).',
        );
      }

      const hue = parseInt(hsl[1], 10);
      const saturation = parseFloat(hsl[2]) / 100;
      const lightness = parseFloat(hsl[3]) / 100;

      ({ red, green, blue } = convertHSLtoRGB(hue, saturation, lightness));
      break;
  }

  return { red, green, blue };
}

/** Adds the requested alpha component to the provided color.
 * @param color - The CSS color to convert. Can be hexadecimal, rgb, rgba, hsl,hsla. CSS
 *        color names are NOT supported.
 * @param alpha - The alpha component to add on to the color. Must be between
 *        [0-1] if alpha is a number. If alpha is a string, it must be a percentage ending in a
 *        percent sign.
 * @returns Returns the color with the alpha component added in rgba format.
 */
export function addAlphaComponent(color: string, alpha: number | string) {
  let newAlpha = alpha;
  if (typeof newAlpha === 'string') {
    const match = /^(\d+)%$/.exec(newAlpha);
    if (!match) {
      throw new Error(
        'When alpha is a string, it must be a whole number ending with a percentage sign',
      );
    }

    newAlpha = parseInt(match[1], 10) / 100;
  } else if (typeof alpha === 'number' && (alpha > 1 || alpha < 0)) {
    throw new Error('When alpha is a number, it must be a decimal between 0 and 1, inclusive.');
  }

  const { red, green, blue } = getRGBComponents(color);
  return `rgba(${red}, ${green}, ${blue}, ${newAlpha})`;
}
